"use client";

import React, { useState, useRef, useEffect } from "react";
import {
  FileText,
  Clock,
  BookOpen,
  Settings,
  CreditCard,
  MoreHorizontal,
  Car,
  MapPin,
  Calendar,
  FileCheck,
  XCircle,
  RefreshCw,
  FileWarning,
  Users,
  Plane,
  Building,
  Map,
  Package,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Globe,
  Flag,
  Navigation,
  Building2,
  UserCheck,
  Percent,
  Coffee,
  Cog,
  Palette,
  Zap,
  Layers,
  Eye,
  Grid3X3,    // ← Added
  List,       // ← Added
} from "lucide-react";

import AddLocalPackageFare from "@/components/Masters/AddLocalPackageFare";
import ManageVehicleType from "@/components/Masters/ManageVehicleType";
import ManageVehicleName from "@/components/Masters/ManageVehicleName";
import ManageVehicleAmenities from "@/components/Masters/ManageVehicleAmenities";
import ManagePaymentStructure from "@/components/Masters/ManagePaymentStructure";
import ManageDocumentation from "@/components/Masters/ManageDocumentation";
import ManageCancellationPolicy from "@/components/Masters/ManageCancellationPolicy";
import ManageRefundPolicy from "@/components/Masters/ManageRefundPolicy";
import ManageTermConditions from "@/components/Masters/ManageTermConditions";
import ManageTransferMaster from "@/components/Masters/ManageTransferMaster";
import ManageCityInstruction from "@/components/Masters/ManageCityInstruction";
import AirportMaster from "@/components/Masters/AirportMaster";
import ManageModules from "@/components/Masters/ManageModules";
import CityActivityMaster from "@/components/Masters/CityActivityMaster";
import CountryDescriptionMaster from "@/components/Masters/CountryDescriptionMaster";
import CoupanTypeMaster from "@/components/Masters/CoupanTypeMaster";
import CouponMaster from "@/components/Masters/CouponMaster";
import MasterBookingType from "@/components/Masters/MasterBookingType";
import ManageLocation from "@/components/Masters/masterLocation";
import MasterAddress from "@/components/Masters/MasterAddress";
import EntranceTicketForm from "@/components/EntranceTicketForm";


// Mock component for demonstration
const MockComponent = ({ title }: { title: string }) => (
  <div className="p-8 text-center">
    <div className="max-w-md mx-auto">
      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Package className="w-8 h-8 text-blue-600" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600">
        This is a placeholder for the {title} component. The actual
        implementation would go here.
      </p>
    </div>
  </div>
);

interface TabItem {
  id: string;
  label: string;
  shortLabel?: string;
  icon: React.ReactNode;
  category: string;
  color?: string;
}

interface DesignSettings {
  cardStyle: "flat" | "elevated" | "3d" | "glass" | "neon";
  animation: "none" | "hover" | "pulse" | "bounce" | "glow";
  borderRadius: "none" | "small" | "medium" | "large" | "full";
  iconSize: "small" | "medium" | "large";
  headerStyle: "solid" | "gradient" | "transparent";
  theme: "red" | "blue" | "green" | "purple" | "orange" | "dark";
}

const Masters: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showDesignPanel, setShowDesignPanel] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid"); // ← New State

  const [designSettings, setDesignSettings] = useState<DesignSettings>({
    cardStyle: "3d",
    animation: "hover",
    borderRadius: "medium",
    iconSize: "medium",
    headerStyle: "gradient",
    theme: "red",
  });

  const tabs: TabItem[] = [
    {
      id: "add-local-package",
      label: "Add Local Package Fare",
      shortLabel: "Package Fare",
      icon: <Package size={24} />,
      category: "package",
      color: "from-purple-500 to-pink-500",
    },
    {
      id: "manage-vehicle-type",
      label: "Manage Vehicle Type",
      shortLabel: "Vehicle Type",
      icon: <Car size={24} />,
      category: "vehicle",
      color: "from-blue-500 to-cyan-500",
    },
    {
      id: "manage-vehicle-name",
      label: "Manage Vehicle Name",
      shortLabel: "Vehicle Name",
      icon: <Car size={24} />,
      category: "vehicle",
      color: "from-green-500 to-emerald-500",
    },
    {
      id: "manage-vehicle-amenities",
      label: "Manage Vehicle Amenities",
      shortLabel: "Amenities",
      icon: <Settings size={24} />,
      category: "vehicle",
      color: "from-orange-500 to-red-500",
    },
    {
      id: "manage-payment-structure",
      label: "Manage Payment Structure",
      shortLabel: "Payment",
      icon: <CreditCard size={24} />,
      category: "payment",
      color: "from-indigo-500 to-purple-500",
    },
    {
      id: "manage-documentation",
      label: "Manage Documentation",
      shortLabel: "Documents",
      icon: <FileCheck size={24} />,
      category: "document",
      color: "from-teal-500 to-blue-500",
    },
    {
      id: "manage-cancellation-policy",
      label: "Manage Cancellation Policy",
      shortLabel: "Cancellation",
      icon: <XCircle size={24} />,
      category: "policy",
      color: "from-red-500 to-pink-500",
    },
    {
      id: "manage-refund-policy",
      label: "Manage Refund Policy",
      shortLabel: "Refund Policy",
      icon: <RefreshCw size={24} />,
      category: "policy",
      color: "from-yellow-500 to-orange-500",
    },
    {
      id: "manage-term-conditions",
      label: "Manage Terms & Conditions",
      shortLabel: "Terms & Conditions",
      icon: <FileText size={24} />,
      category: "legal",
      color: "from-gray-500 to-slate-500",
    },
    {
      id: "manage-transfer-master",
      label: "Manage Transfer Master",
      shortLabel: "Transfer Master",
      icon: <Car size={24} />,
      category: "vehicle",
      color: "from-cyan-500 to-blue-500",
    },
    {
      id: "manage-city-instruction",
      label: "Manage City Instruction",
      shortLabel: "City Instruction",
      icon: <MapPin size={24} />,
      category: "location",
      color: "from-emerald-500 to-teal-500",
    },
    {
      id: "manage-modules",
      label: "Manage Modules",
      shortLabel: "Modules",
      icon: <Settings size={24} />,
      category: "system",
      color: "from-violet-500 to-purple-500",
    },
    {
      id: "airport-master",
      label: "Airport Master",
      shortLabel: "Airport Master",
      icon: <Plane size={24} />,
      category: "location",
      color: "from-sky-500 to-blue-500",
    },
    {
      id: "city-activity-master",
      label: "City Activity Master",
      shortLabel: "City Activity",
      icon: <Map size={24} />,
      category: "activity",
      color: "from-lime-500 to-green-500",
    },
    {
      id: "country-description-master",
      label: "Country Description Master",
      shortLabel: "Country Description",
      icon: <BookOpen size={24} />,
      category: "content",
      color: "from-amber-500 to-yellow-500",
    },
    {
      id: "coupon-type",
      label: "Coupon Type Master",
      shortLabel: "Coupon Type Master",
      icon: <BookOpen size={24} />,
      category: "content",
      color: "from-amber-500 to-yellow-500",
    },
    {
      id: "coupon",
      label: "Coupon Master",
      shortLabel: "Coupon Master",
      icon: <BookOpen size={24} />,
      category: "content",
      color: "from-amber-500 to-yellow-500",
    },
    {
      id: "master-booking-type",
      label: "Master Booking Type",
      shortLabel: "Booking Type",
      icon: <Car size={24} />,
      category: "master-booking-type",
      color: "from-green-500 to-emerald-500",
    },
    {
      id: "master-location",
      label: "Master Location",
      shortLabel: "Location",
      icon: <MapPin size={24} />,
      category: "location",
      color: "from-emerald-500 to-teal-500",
    },
    {
      id: "master-address",
      label: "Master Address",
      shortLabel: "Address",
      icon: <MapPin size={24} />,
      category: "address",
      color: "from-emerald-500 to-teal-500",
    },
    {
      id: "entrance-ticket",
      label: "Entrance Ticket",
      shortLabel: "Entrance",
      icon: <MapPin size={24} />,
      category: "entrance-ticket",
      color: "from-green-500 to-emerald-500"
    }
  ];

  // Sort tabs alphabetically by label
  const sortedTabs = [...tabs].sort((a, b) => a.label.localeCompare(b.label));

  const categories = [
    { id: "all", label: "All", count: sortedTabs.length },
    {
      id: "location",
      label: "Location",
      count: sortedTabs.filter((t) => t.category === "location").length,
    },
    {
      id: "vehicle",
      label: "Vehicle",
      count: sortedTabs.filter((t) => t.category === "vehicle").length,
    },
    {
      id: "payment",
      label: "Payment",
      count: sortedTabs.filter((t) => t.category === "payment").length,
    },
    {
      id: "supplier",
      label: "Supplier",
      count: sortedTabs.filter((t) => t.category === "supplier").length,
    },
    {
      id: "legal",
      label: "Legal",
      count: sortedTabs.filter((t) => t.category === "legal").length,
    },
  ];

  const themeColors = {
    red: "from-red-500 to-rose-500",
    blue: "from-blue-500 to-indigo-500",
    green: "from-green-500 to-emerald-500",
    purple: "from-purple-500 to-violet-500",
    orange: "from-orange-500 to-amber-500",
    dark: "from-gray-800 to-slate-800",
  };

  const filteredTabs = sortedTabs.filter((tab) => {
    const matchesSearch =
      tab.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tab.shortLabel &&
        tab.shortLabel.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory =
      selectedCategory === "all" || tab.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleCardClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  const handleBackToGrid = () => {
    setActiveTab(null);
  };

  const getCurrentTabData = () => {
    return sortedTabs.find((tab) => tab.id === activeTab);
  };

  const getCardClasses = (tab: TabItem) => {
    const baseClasses =
      "cursor-pointer group overflow-hidden transition-all duration-300";

    let cardClasses = baseClasses;

    const radiusMap = {
      none: "",
      small: "rounded-sm",
      medium: "rounded-lg",
      large: "rounded-xl",
      full: "rounded-3xl",
    };
    cardClasses += ` ${radiusMap[designSettings.borderRadius]}`;

    switch (designSettings.cardStyle) {
      case "flat":
        cardClasses += " bg-white border border-gray-200";
        break;
      case "elevated":
        cardClasses +=
          " bg-white shadow-lg hover:shadow-xl border border-gray-100";
        break;
      case "3d":
        cardClasses +=
          " bg-white shadow-lg hover:shadow-2xl transform hover:-translate-y-2 hover:rotate-1 border border-gray-100";
        break;
      case "glass":
        cardClasses +=
          " bg-white/80 backdrop-blur-sm border border-white/20 shadow-lg hover:shadow-xl";
        break;
      case "neon":
        cardClasses +=
          " bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-cyan-400/50 shadow-lg hover:shadow-cyan-400/25";
        break;
    }

    switch (designSettings.animation) {
      case "hover":
        cardClasses += " hover:scale-105";
        break;
      case "pulse":
        cardClasses += " animate-pulse hover:animate-none";
        break;
      case "bounce":
        cardClasses += " hover:animate-bounce";
        break;
      case "glow":
        cardClasses += " hover:shadow-2xl hover:shadow-current";
        break;
    }

    return cardClasses;
  };

  const getHeaderClasses = (tab: TabItem) => {
    let headerClasses = "text-white p-3 text-center";

    switch (designSettings.headerStyle) {
      case "solid":
        headerClasses += ` bg-${designSettings.theme}-600`;
        break;
      case "gradient":
        headerClasses += ` bg-gradient-to-r ${
          tab.color || themeColors[designSettings.theme]
        }`;
        break;
      case "transparent":
        headerClasses +=
          " bg-transparent text-gray-800 border-b border-gray-200";
        break;
    }

    return headerClasses;
  };

  const getIconSize = () => {
    const sizeMap = {
      small: 20,
      medium: 24,
      large: 32,
    };
    return sizeMap[designSettings.iconSize];
  };

  const renderDesignPanel = () => (
    <div
      className={`fixed right-0 top-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 z-50 ${
        showDesignPanel ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Design Settings
          </h3>
          <button
            onClick={() => setShowDesignPanel(false)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="p-6 space-y-6 overflow-y-auto h-full pb-20">
        {/* ... (your existing design panel code remains unchanged) */}
        {/* Card Style */}
        <div>
          <label className="block text-sm font-medium mb-3">Card Style</label>
          <div className="grid grid-cols-2 gap-2">
            {(["flat", "elevated", "3d", "glass", "neon"] as const).map(
              (style) => (
                <button
                  key={style}
                  onClick={() =>
                    setDesignSettings((prev) => ({ ...prev, cardStyle: style }))
                  }
                  className={`p-3 text-xs rounded-lg border-2 transition-colors ${
                    designSettings.cardStyle === style
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {style.charAt(0).toUpperCase() + style.slice(1)}
                </button>
              )
            )}
          </div>
        </div>
        {/* ... rest of design panel unchanged ... */}
      </div>
    </div>
  );

  const renderTabContent = () => {
    const currentTab = getCurrentTabData();

    const renderComponent = () => {
      if (!currentTab) return <div>No tab selected</div>;

      switch (currentTab.id) {
        case "add-local-package":
          return <AddLocalPackageFare />;
        case "manage-vehicle-type":
          return <ManageVehicleType />;
        case "manage-vehicle-name":
          return <ManageVehicleName />;
        case "manage-vehicle-amenities":
          return <ManageVehicleAmenities />;
        case "manage-payment-structure":
          return <ManagePaymentStructure />;
        case "manage-documentation":
          return <ManageDocumentation />;
        case "manage-cancellation-policy":
          return <ManageCancellationPolicy />;
        case "manage-refund-policy":
          return <ManageRefundPolicy />;
        case "manage-term-conditions":
          return <ManageTermConditions />;
        case "manage-transfer-master":
          return <ManageTransferMaster />;
        case "manage-city-instruction":
          return <ManageCityInstruction />;
        case "manage-modules":
          return <ManageModules />;
        case "airport-master":
          return <AirportMaster />;
        case "city-activity-master":
          return <CityActivityMaster />;
        case "country-description-master":
          return <CountryDescriptionMaster />;
        case "coupon-type":
          return <CoupanTypeMaster />;
        case "coupon":
          return <CouponMaster />;
        case "master-booking-type":
          return <MasterBookingType />;
        case "master-location":
          return <ManageLocation />;
        case "master-address":
          return <MasterAddress />;
        
        case "entrance-ticket":
          return <EntranceTicketForm/>
        default:
          return <MockComponent title={currentTab.label} />;
      }
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={handleBackToGrid}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft size={16} />
            Back to Masters
          </button>
          <div className="h-6 w-px bg-gray-300"></div>
          <div className="flex items-center gap-2">
            <div className="text-red-600">
              {currentTab?.icon &&
                React.cloneElement(currentTab.icon as React.ReactElement)}
            </div>
            <span className="text-lg font-semibold text-gray-900">
              {currentTab?.label}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="min-h-[500px] p-6">{renderComponent()}</div>
        </div>
      </div>
    );
  };

  if (activeTab) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="w-full max-w-7xl mx-auto px-4 py-6">
          {renderTabContent()}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="w-full max-w-7xl mx-auto px-4 py-6">
        {/* Header with View Toggle + Settings */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Masters</h1>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-white rounded-lg shadow-sm border border-gray-200 p-1">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-md transition-all ${
                  viewMode === "grid"
                    ? "bg-blue-100 text-blue-600 shadow-sm"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                title="Grid View"
              >
                <Grid3X3 size={18} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-md transition-all ${
                  viewMode === "list"
                    ? "bg-blue-100 text-blue-600 shadow-sm"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                title="List View"
              >
                <List size={18} />
              </button>
            </div>

            <button
              onClick={() => setShowDesignPanel(true)}
              className="relative p-3 bg-white rounded-full shadow-lg hover:shadow-xl border border-gray-200 hover:border-gray-300 transition-all duration-300 group"
              title="Design Settings"
            >
              <Settings className="w-6 h-6 text-gray-600 group-hover:text-blue-600 transition-colors duration-300 group-hover:rotate-90" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm mb-6 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="text"
                  placeholder="Search masters..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      selectedCategory === category.id
                        ? "bg-blue-100 text-blue-700"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {category.label}
                    <span className="ml-1 text-xs opacity-75">
                      ({category.count})
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Grid View */}
        {viewMode === "grid" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
            {filteredTabs.map((tab) => (
              <div
                key={tab.id}
                onClick={() => handleCardClick(tab.id)}
                className={getCardClasses(tab)}
              >
                <div className={getHeaderClasses(tab)}>
                  <h3 className="text-sm font-semibold truncate">
                    {tab.shortLabel || tab.label}
                  </h3>
                </div>

                <div
                  className={`p-8 flex items-center justify-center transition-all duration-300 ${
                    designSettings.cardStyle === "neon"
                      ? "bg-gray-800/50"
                      : "bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-gray-100 group-hover:to-gray-50"
                  }`}
                >
                  <div
                    className={`transition-all duration-300 ${
                      designSettings.cardStyle === "neon"
                        ? "text-cyan-400 group-hover:text-cyan-300"
                        : "text-gray-700 group-hover:text-blue-600"
                    }`}
                  >
                    {React.cloneElement(tab.icon as React.ReactElement, {
                      size: getIconSize(),
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* List View */}
        {viewMode === "list" && (
          <div className="space-y-3">
            {filteredTabs.map((tab) => (
              <div
                key={tab.id}
                onClick={() => handleCardClick(tab.id)}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer group flex items-center gap-4 p-4"
              >
                <div className={`p-3 rounded-lg bg-gradient-to-r ${tab.color || themeColors.red}`}>
                  <div className="text-white">
                    {React.cloneElement(tab.icon as React.ReactElement, { size: 20 })}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{tab.label}</h3>
                  {tab.shortLabel && (
                    <p className="text-sm text-gray-500">{tab.shortLabel}</p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
              </div>
            ))}
          </div>
        )}

        {/* No Results */}
        {filteredTabs.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No masters found
            </h3>
            <p className="text-gray-600">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}

        {/* Results Count */}
        {filteredTabs.length > 0 && (
          <div className="mt-6 text-center text-sm text-gray-600">
            Showing {filteredTabs.length} of {sortedTabs.length} masters
          </div>
        )}
      </div>

      {/* Design Panel & Backdrop */}
      {renderDesignPanel()}
      {showDesignPanel && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setShowDesignPanel(false)}
        />
      )}
    </div>
  );
};

export default Masters;

// "use client";

// import React, { useState, useRef, useEffect } from "react";
// import {
//   FileText,
//   Clock,
//   BookOpen,
//   Settings,
//   CreditCard,
//   MoreHorizontal,
//   Car,
//   MapPin,
//   Calendar,
//   FileCheck,
//   XCircle,
//   RefreshCw,
//   FileWarning,
//   Users,
//   Plane,
//   Building,
//   Map,
//   Package,
//   Menu,
//   X,
//   ChevronLeft,
//   ChevronRight,
//   Search,
//   Filter,
//   Globe,
//   Flag,
//   Navigation,
//   Building2,
//   UserCheck,
//   Percent,
//   Coffee,
//   Cog,
//   Palette,
//   Zap,
//   Layers,
//   Eye,
// } from "lucide-react";
// import { strict } from "assert";
// import AddLocalPackageFare from "@/components/Masters/AddLocalPackageFare";
// import ManageVehicleType from "@/components/Masters/ManageVehicleType";
// import ManageVehicleName from "@/components/Masters/ManageVehicleName";
// import ManageVehicleAmenities from "@/components/Masters/ManageVehicleAmenities";
// import ManagePaymentStructure from "@/components/Masters/ManagePaymentStructure";
// import ManageDocumentation from "@/components/Masters/ManageDocumentation";
// import ManageCancellationPolicy from "@/components/Masters/ManageCancellationPolicy";
// import ManageRefundPolicy from "@/components/Masters/ManageRefundPolicy";
// import ManageTermConditions from "@/components/Masters/ManageTermConditions";
// import ManageTransferMaster from "@/components/Masters/ManageTransferMaster";
// import ManageCityInstruction from "@/components/Masters/ManageCityInstruction";
// import AirportMaster from "@/components/Masters/AirportMaster";
// import ManageModules from "@/components/Masters/ManageModules";
// import CityActivityMaster from "@/components/Masters/CityActivityMaster";
// import CountryDescriptionMaster from "@/components/Masters/CountryDescriptionMaster";
// import CoupanTypeMaster from "@/components/Masters/CoupanTypeMaster";
// import CouponMaster from "@/components/Masters/CouponMaster";
// import MasterBookingType from "@/components/Masters/MasterBookingType";
// import ManageLocation from "@/components/Masters/masterLocation";
// import MasterAddress from "@/components/Masters/MasterAddress";

// // Mock component for demonstration
// const MockComponent = ({ title }: { title: string }) => (
//   <div className="p-8 text-center">
//     <div className="max-w-md mx-auto">
//       <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
//         <Package className="w-8 h-8 text-blue-600" />
//       </div>
//       <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
//       <p className="text-gray-600">
//         This is a placeholder for the {title} component. The actual
//         implementation would go here.
//       </p>
//     </div>
//   </div>
// );

// interface TabItem {
//   id: string;
//   label: string;
//   shortLabel?: string;
//   icon: React.ReactNode;
//   category: string;
//   color?: string;
// }

// interface DesignSettings {
//   cardStyle: "flat" | "elevated" | "3d" | "glass" | "neon";
//   animation: "none" | "hover" | "pulse" | "bounce" | "glow";
//   borderRadius: "none" | "small" | "medium" | "large" | "full";
//   iconSize: "small" | "medium" | "large";
//   headerStyle: "solid" | "gradient" | "transparent";
//   theme: "red" | "blue" | "green" | "purple" | "orange" | "dark";
// }

// const Masters: React.FC = () => {
//   const [activeTab, setActiveTab] = useState<string | null>(null);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [selectedCategory, setSelectedCategory] = useState("all");
//   const [showDesignPanel, setShowDesignPanel] = useState(false);
//   const [designSettings, setDesignSettings] = useState<DesignSettings>({
//     cardStyle: "3d",
//     animation: "hover",
//     borderRadius: "medium",
//     iconSize: "medium",
//     headerStyle: "gradient",
//     theme: "red",
//   });

//   const tabs: TabItem[] = [
//     {
//       id: "add-local-package",
//       label: "Add Local Package Fare",
//       shortLabel: "Package Fare",
//       icon: <Package size={24} />,
//       category: "package",
//       color: "from-purple-500 to-pink-500",
//     },
//     {
//       id: "manage-vehicle-type",
//       label: "Manage Vehicle Type",
//       shortLabel: "Vehicle Type",
//       icon: <Car size={24} />,
//       category: "vehicle",
//       color: "from-blue-500 to-cyan-500",
//     },
//     {
//       id: "manage-vehicle-name",
//       label: "Manage Vehicle Name",
//       shortLabel: "Vehicle Name",
//       icon: <Car size={24} />,
//       category: "vehicle",
//       color: "from-green-500 to-emerald-500",
//     },
//     {
//       id: "manage-vehicle-amenities",
//       label: "Manage Vehicle Amenities",
//       shortLabel: "Amenities",
//       icon: <Settings size={24} />,
//       category: "vehicle",
//       color: "from-orange-500 to-red-500",
//     },
//     {
//       id: "manage-payment-structure",
//       label: "Manage Payment Structure",
//       shortLabel: "Payment",
//       icon: <CreditCard size={24} />,
//       category: "payment",
//       color: "from-indigo-500 to-purple-500",
//     },
//     {
//       id: "manage-documentation",
//       label: "Manage Documentation",
//       shortLabel: "Documents",
//       icon: <FileCheck size={24} />,
//       category: "document",
//       color: "from-teal-500 to-blue-500",
//     },
//     {
//       id: "manage-cancellation-policy",
//       label: "Manage Cancellation Policy",
//       shortLabel: "Cancellation",
//       icon: <XCircle size={24} />,
//       category: "policy",
//       color: "from-red-500 to-pink-500",
//     },
//     {
//       id: "manage-refund-policy",
//       label: "Manage Refund Policy",
//       shortLabel: "Refund Policy",
//       icon: <RefreshCw size={24} />,
//       category: "policy",
//       color: "from-yellow-500 to-orange-500",
//     },
//     {
//       id: "manage-term-conditions",
//       label: "Manage Terms & Conditions",
//       shortLabel: "Terms & Conditions",
//       icon: <FileText size={24} />,
//       category: "legal",
//       color: "from-gray-500 to-slate-500",
//     },
//     {
//       id: "manage-transfer-master",
//       label: "Manage Transfer Master",
//       shortLabel: "Transfer Master",
//       icon: <Car size={24} />,
//       category: "vehicle",
//       color: "from-cyan-500 to-blue-500",
//     },
//     {
//       id: "manage-city-instruction",
//       label: "Manage City Instruction",
//       shortLabel: "City Instruction",
//       icon: <MapPin size={24} />,
//       category: "location",
//       color: "from-emerald-500 to-teal-500",
//     },
//     {
//       id: "manage-modules",
//       label: "Manage Modules",
//       shortLabel: "Modules",
//       icon: <Settings size={24} />,
//       category: "system",
//       color: "from-violet-500 to-purple-500",
//     },
//     {
//       id: "airport-master",
//       label: "Airport Master",
//       shortLabel: "Airport Master",
//       icon: <Plane size={24} />,
//       category: "location",
//       color: "from-sky-500 to-blue-500",
//     },
//     {
//       id: "city-activity-master",
//       label: "City Activity Master",
//       shortLabel: "City Activity",
//       icon: <Map size={24} />,
//       category: "activity",
//       color: "from-lime-500 to-green-500",
//     },
//     {
//       id: "country-description-master",
//       label: "Country Description Master",
//       shortLabel: "Country Description",
//       icon: <BookOpen size={24} />,
//       category: "content",
//       color: "from-amber-500 to-yellow-500",
//     },
//     {
//       id: "coupon-type",
//       label: "Coupon Type Master",
//       shortLabel: "Coupon Type Master",
//       icon: <BookOpen size={24} />,
//       category: "content",
//       color: "from-amber-500 to-yellow-500",
//     },
//     {
//       id: "coupon",
//       label: "Coupon Master",
//       shortLabel: "Coupon Master",
//       icon: <BookOpen size={24} />,
//       category: "content",
//       color: "from-amber-500 to-yellow-500",
//     },
//     {
//       id: "master-booking-type",
//       label: "Master Booking Type",
//       shortLabel: "Booking Type",
//       icon: <Car size={24} />,
//       category: "master-booking-type",
//       color: "from-green-500 to-emerald-500",
//     },
//     {
//       id: "master-location",
//       label: "Master Location",
//       shortLabel: "Location",
//       icon: <MapPin size={24} />,
//       category: "location",
//       color: "from-emerald-500 to-teal-500",
//     },
//     {
//       id: "master-address",
//       label: "Master Address",
//       shortLabel: "Address",
//       icon: <MapPin size={24} />,
//       category: "address",
//       color: "from-emerald-500 to-teal-500",
//     }
//   ];

//   const categories = [
//     { id: "all", label: "All", count: tabs.length },
//     {
//       id: "location",
//       label: "Location",
//       count: tabs.filter((t) => t.category === "location").length,
//     },
//     {
//       id: "vehicle",
//       label: "Vehicle",
//       count: tabs.filter((t) => t.category === "vehicle").length,
//     },
//     {
//       id: "payment",
//       label: "Payment",
//       count: tabs.filter((t) => t.category === "payment").length,
//     },
//     {
//       id: "supplier",
//       label: "Supplier",
//       count: tabs.filter((t) => t.category === "supplier").length,
//     },
//     {
//       id: "legal",
//       label: "Legal",
//       count: tabs.filter((t) => t.category === "legal").length,
//     },
//   ];

//   const themeColors = {
//     red: "from-red-500 to-rose-500",
//     blue: "from-blue-500 to-indigo-500",
//     green: "from-green-500 to-emerald-500",
//     purple: "from-purple-500 to-violet-500",
//     orange: "from-orange-500 to-amber-500",
//     dark: "from-gray-800 to-slate-800",
//   };

//   const filteredTabs = tabs.filter((tab) => {
//     const matchesSearch =
//       tab.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       (tab.shortLabel &&
//         tab.shortLabel.toLowerCase().includes(searchTerm.toLowerCase()));
//     const matchesCategory =
//       selectedCategory === "all" || tab.category === selectedCategory;
//     return matchesSearch && matchesCategory;
//   });

//   const handleCardClick = (tabId: string) => {
//     setActiveTab(tabId);
//   };

//   const handleBackToGrid = () => {
//     setActiveTab(null);
//   };

//   const getCurrentTabData = () => {
//     return tabs.find((tab) => tab.id === activeTab);
//   };

//   const getCardClasses = (tab: TabItem) => {
//     const baseClasses =
//       "cursor-pointer group overflow-hidden transition-all duration-300";

//     let cardClasses = baseClasses;

//     // Border radius
//     const radiusMap = {
//       none: "",
//       small: "rounded-sm",
//       medium: "rounded-lg",
//       large: "rounded-xl",
//       full: "rounded-3xl",
//     };
//     cardClasses += ` ${radiusMap[designSettings.borderRadius]}`;

//     // Card style
//     switch (designSettings.cardStyle) {
//       case "flat":
//         cardClasses += " bg-white border border-gray-200";
//         break;
//       case "elevated":
//         cardClasses +=
//           " bg-white shadow-lg hover:shadow-xl border border-gray-100";
//         break;
//       case "3d":
//         cardClasses +=
//           " bg-white shadow-lg hover:shadow-2xl transform hover:-translate-y-2 hover:rotate-1 border border-gray-100";
//         break;
//       case "glass":
//         cardClasses +=
//           " bg-white/80 backdrop-blur-sm border border-white/20 shadow-lg hover:shadow-xl";
//         break;
//       case "neon":
//         cardClasses +=
//           " bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-cyan-400/50 shadow-lg hover:shadow-cyan-400/25";
//         break;
//     }

//     // Animation
//     switch (designSettings.animation) {
//       case "hover":
//         cardClasses += " hover:scale-105";
//         break;
//       case "pulse":
//         cardClasses += " animate-pulse hover:animate-none";
//         break;
//       case "bounce":
//         cardClasses += " hover:animate-bounce";
//         break;
//       case "glow":
//         cardClasses += " hover:shadow-2xl hover:shadow-current";
//         break;
//     }

//     return cardClasses;
//   };

//   const getHeaderClasses = (tab: TabItem) => {
//     let headerClasses = "text-white p-3 text-center";

//     switch (designSettings.headerStyle) {
//       case "solid":
//         headerClasses += ` bg-${designSettings.theme}-600`;
//         break;
//       case "gradient":
//         headerClasses += ` bg-gradient-to-r ${
//           tab.color || themeColors[designSettings.theme]
//         }`;
//         break;
//       case "transparent":
//         headerClasses +=
//           " bg-transparent text-gray-800 border-b border-gray-200";
//         break;
//     }

//     return headerClasses;
//   };

//   const getIconSize = () => {
//     const sizeMap = {
//       small: 20,
//       medium: 24,
//       large: 32,
//     };
//     return sizeMap[designSettings.iconSize];
//   };

//   const renderDesignPanel = () => (
//     <div
//       className={`fixed right-0 top-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 z-50 ${
//         showDesignPanel ? "translate-x-0" : "translate-x-full"
//       }`}
//     >
//       <div className="p-6 border-b">
//         <div className="flex items-center justify-between">
//           <h3 className="text-lg font-semibold flex items-center gap-2">
//             <Palette className="w-5 h-5" />
//             Design Settings
//           </h3>
//           <button
//             onClick={() => setShowDesignPanel(false)}
//             className="p-2 hover:bg-gray-100 rounded-lg"
//           >
//             <X className="w-5 h-5" />
//           </button>
//         </div>
//       </div>
//       <div className="p-6 space-y-6 overflow-y-auto h-full pb-20">
//         {/* Card Style */}
//         <div>
//           <label className="block text-sm font-medium mb-3">Card Style</label>
//           <div className="grid grid-cols-2 gap-2">
//             {(["flat", "elevated", "3d", "glass", "neon"] as const).map(
//               (style) => (
//                 <button
//                   key={style}
//                   onClick={() =>
//                     setDesignSettings((prev) => ({ ...prev, cardStyle: style }))
//                   }
//                   className={`p-3 text-xs rounded-lg border-2 transition-colors ${
//                     designSettings.cardStyle === style
//                       ? "border-blue-500 bg-blue-50"
//                       : "border-gray-200 hover:border-gray-300"
//                   }`}
//                 >
//                   {style.charAt(0).toUpperCase() + style.slice(1)}
//                 </button>
//               )
//             )}
//           </div>
//         </div>

//         {/* Animation */}
//         <div>
//           <label className="block text-sm font-medium mb-3">Animation</label>
//           <div className="grid grid-cols-2 gap-2">
//             {(["none", "hover", "pulse", "bounce", "glow"] as const).map(
//               (animation) => (
//                 <button
//                   key={animation}
//                   onClick={() =>
//                     setDesignSettings((prev) => ({ ...prev, animation }))
//                   }
//                   className={`p-3 text-xs rounded-lg border-2 transition-colors ${
//                     designSettings.animation === animation
//                       ? "border-blue-500 bg-blue-50"
//                       : "border-gray-200 hover:border-gray-300"
//                   }`}
//                 >
//                   {animation.charAt(0).toUpperCase() + animation.slice(1)}
//                 </button>
//               )
//             )}
//           </div>
//         </div>

//         {/* Border Radius */}
//         <div>
//           <label className="block text-sm font-medium mb-3">
//             Border Radius
//           </label>
//           <div className="grid grid-cols-2 gap-2">
//             {(["none", "small", "medium", "large", "full"] as const).map(
//               (radius) => (
//                 <button
//                   key={radius}
//                   onClick={() =>
//                     setDesignSettings((prev) => ({
//                       ...prev,
//                       borderRadius: radius,
//                     }))
//                   }
//                   className={`p-3 text-xs rounded-lg border-2 transition-colors ${
//                     designSettings.borderRadius === radius
//                       ? "border-blue-500 bg-blue-50"
//                       : "border-gray-200 hover:border-gray-300"
//                   }`}
//                 >
//                   {radius.charAt(0).toUpperCase() + radius.slice(1)}
//                 </button>
//               )
//             )}
//           </div>
//         </div>

//         {/* Theme */}
//         <div>
//           <label className="block text-sm font-medium mb-3">Theme Color</label>
//           <div className="grid grid-cols-3 gap-2">
//             {(Object.keys(themeColors) as Array<keyof typeof themeColors>).map(
//               (theme) => (
//                 <button
//                   key={theme}
//                   onClick={() =>
//                     setDesignSettings((prev) => ({ ...prev, theme }))
//                   }
//                   className={`p-3 text-xs rounded-lg border-2 transition-colors ${
//                     designSettings.theme === theme
//                       ? "border-blue-500 bg-blue-50"
//                       : "border-gray-200 hover:border-gray-300"
//                   }`}
//                 >
//                   <div
//                     className={`w-4 h-4 rounded-full bg-gradient-to-r ${themeColors[theme]} mx-auto mb-1`}
//                   ></div>
//                   {theme.charAt(0).toUpperCase() + theme.slice(1)}
//                 </button>
//               )
//             )}
//           </div>
//         </div>

//         {/* Header Style */}
//         <div>
//           <label className="block text-sm font-medium mb-3">Header Style</label>
//           <div className="grid grid-cols-2 gap-2">
//             {(["solid", "gradient", "transparent"] as const).map((style) => (
//               <button
//                 key={style}
//                 onClick={() =>
//                   setDesignSettings((prev) => ({ ...prev, headerStyle: style }))
//                 }
//                 className={`p-3 text-xs rounded-lg border-2 transition-colors ${
//                   designSettings.headerStyle === style
//                     ? "border-blue-500 bg-blue-50"
//                     : "border-gray-200 hover:border-gray-300"
//                 }`}
//               >
//                 {style.charAt(0).toUpperCase() + style.slice(1)}
//               </button>
//             ))}
//           </div>
//         </div>

//         {/* Icon Size */}
//         <div>
//           <label className="block text-sm font-medium mb-3">Icon Size</label>
//           <div className="grid grid-cols-3 gap-2">
//             {(["small", "medium", "large"] as const).map((size) => (
//               <button
//                 key={size}
//                 onClick={() =>
//                   setDesignSettings((prev) => ({ ...prev, iconSize: size }))
//                 }
//                 className={`p-3 text-xs rounded-lg border-2 transition-colors ${
//                   designSettings.iconSize === size
//                     ? "border-blue-500 bg-blue-50"
//                     : "border-gray-200 hover:border-gray-300"
//                 }`}
//               >
//                 {size.charAt(0).toUpperCase() + size.slice(1)}
//               </button>
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );

//   const renderTabContent = () => {
//     const currentTab = getCurrentTabData();

//     const renderComponent = () => {
//       const currentTab = getCurrentTabData();
//       if (!currentTab) return <div>No tab selected</div>;

//       switch (currentTab.id) {
//         case "add-local-package":
//           return <AddLocalPackageFare />;
//         case "manage-vehicle-type":';'
//           return <ManageVehicleType />;
//         case "manage-vehicle-name":
//           return <ManageVehicleName />;
//         case "manage-vehicle-amenities":
//           return <ManageVehicleAmenities />;
//         case "manage-payment-structure":
//           return <ManagePaymentStructure />;
//         case "manage-documentation":
//           return <ManageDocumentation />;
//         case "manage-cancellation-policy":
//           return <ManageCancellationPolicy />;
//         case "manage-refund-policy":
//           return <ManageRefundPolicy />;
//         case "manage-term-conditions":
//           return <ManageTermConditions />;
//         case "manage-transfer-master":
//           return <ManageTransferMaster />;
//         case "manage-city-instruction":
//           return <ManageCityInstruction />;
//         case "manage-modules":
//           return <ManageModules />;
//         case "airport-master":
//           return <AirportMaster />;
//         case "city-activity-master":
//           return <CityActivityMaster />;
//         case "country-description-master":
//           return <CountryDescriptionMaster />;
//         case "coupon-type": 
//           return <CoupanTypeMaster />

//         case "coupon":
//           return <CouponMaster />
        
//         case "master-booking-type":
//           return <MasterBookingType/>
//         case "master-location":
//           return <ManageLocation/>
//         case "master-address":
//           return <MasterAddress/>
//         default:
//           return <MockComponent title={currentTab.label} />;
//       }
//     };

//     return (
//       <div className="space-y-4">
//         {/* Back Navigation */}
//         <div className="flex items-center gap-3 mb-6">
//           <button
//             onClick={handleBackToGrid}
//             className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
//           >
//             <ChevronLeft size={16} />
//             Back to Masters
//           </button>
//           <div className="h-6 w-px bg-gray-300"></div>
//           <div className="flex items-center gap-2">
//             <div className="text-red-600">
//               {currentTab?.icon &&
//                 React.cloneElement(currentTab.icon as React.ReactElement)}
//             </div>
//             <span className="text-lg font-semibold text-gray-900">
//               {currentTab?.label}
//             </span>
//           </div>
//         </div>

//         <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
//           <div className="min-h-[500px] p-6">{renderComponent()}</div>
//         </div>
//       </div>
//     );
//   };

//   // If a tab is selected, show the content view
//   if (activeTab) {
//     return (
//       <div className="min-h-screen bg-gray-50">
//         <div className="w-full max-w-7xl mx-auto px-4 py-6">
//           {renderTabContent()}
//         </div>
//       </div>
//     );
//   }

//   // Otherwise, show the card grid
//   return (
//     <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
//       <div className="w-full max-w-7xl mx-auto px-4 py-6">
//         {/* Header with Settings Icon */}
//         <div className="flex items-center justify-between mb-6">
//           <h1 className="text-3xl font-bold text-gray-900">Masters</h1>
//           <button
//             onClick={() => setShowDesignPanel(true)}
//             className="relative p-3 bg-white rounded-full shadow-lg hover:shadow-xl border border-gray-200 hover:border-gray-300 transition-all duration-300 group"
//             title="Design Settings"
//           >
//             <Settings className="w-6 h-6 text-gray-600 group-hover:text-blue-600 transition-colors duration-300 group-hover:rotate-90" />
//             <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
//           </button>
//         </div>

//         {/* Search and Filter Bar */}
//         <div className="bg-white rounded-lg shadow-sm mb-6 p-4">
//           <div className="flex items-center justify-between gap-4">
//             <div className="flex items-center gap-4 flex-1">
//               <div className="relative flex-1 max-w-md">
//                 <Search
//                   className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
//                   size={16}
//                 />
//                 <input
//                   type="text"
//                   placeholder="Search masters..."
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                   className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                 />
//               </div>

//               <div className="flex items-center gap-2">
//                 {categories.map((category) => (
//                   <button
//                     key={category.id}
//                     onClick={() => setSelectedCategory(category.id)}
//                     className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
//                       selectedCategory === category.id
//                         ? "bg-blue-100 text-blue-700"
//                         : "bg-gray-100 text-gray-600 hover:bg-gray-200"
//                     }`}
//                   >
//                     {category.label}
//                     <span className="ml-1 text-xs opacity-75">
//                       ({category.count})
//                     </span>
//                   </button>
//                 ))}
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Cards Grid */}
//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
//           {filteredTabs.map((tab) => (
//             <div
//               key={tab.id}
//               onClick={() => handleCardClick(tab.id)}
//               className={getCardClasses(tab)}
//             >
//               {/* Header */}
//               <div className={getHeaderClasses(tab)}>
//                 <h3 className="text-sm font-semibold truncate">
//                   {tab.shortLabel || tab.label}
//                 </h3>
//               </div>

//               {/* Icon Section */}
//               <div
//                 className={`p-8 flex items-center justify-center transition-all duration-300 ${
//                   designSettings.cardStyle === "neon"
//                     ? "bg-gray-800/50"
//                     : "bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-gray-100 group-hover:to-gray-50"
//                 }`}
//               >
//                 <div
//                   className={`transition-all duration-300 ${
//                     designSettings.cardStyle === "neon"
//                       ? "text-cyan-400 group-hover:text-cyan-300"
//                       : "text-gray-700 group-hover:text-blue-600"
//                   }`}
//                 >
//                   {React.cloneElement(tab.icon as React.ReactElement)}
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>

//         {/* No Results */}
//         {filteredTabs.length === 0 && (
//           <div className="text-center py-12">
//             <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
//               <Search className="w-8 h-8 text-gray-400" />
//             </div>
//             <h3 className="text-lg font-semibold text-gray-900 mb-2">
//               No masters found
//             </h3>
//             <p className="text-gray-600">
//               Try adjusting your search or filter criteria
//             </p>
//           </div>
//         )}

//         {/* Results Count */}
//         {filteredTabs.length > 0 && (
//           <div className="mt-6 text-center text-sm text-gray-600">
//             Showing {filteredTabs.length} of {tabs.length} masters
//           </div>
//         )}
//       </div>

//       {/* Design Panel */}
//       {/* {renderDesignPanel()} */}

//       {/* Backdrop */}
//       {showDesignPanel && (
//         <div
//           className="fixed inset-0 bg-black/50 z-40"
//           onClick={() => setShowDesignPanel(false)}
//         />
//       )} 
//     </div>
//   );
// };

// export default Masters;
